import * as React from "react";
import { Box, Button, Paper, Chip, Grid, Divider } from "@mui/material";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import { DateTime } from "luxon";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import RefreshIcon from "@mui/icons-material/Refresh";
import { EnvironmentPicker } from "../../../components/EnvironmentPicker";
import type { IEnvironment } from "../../../types/EnvironmentTypes";
import { IMaintenanceFile, MaintenanceStatus } from "../../../services/registry/MaintenanceService";

interface MaintenanceMessageToolbarProps {
  selectedEnvironment: IEnvironment | undefined;
  onEnvironmentSelected: (environment: IEnvironment | undefined) => void;
  onStartMaintenanceButtonClick: () => void;
  onEndMaintenanceButtonClick: () => void;
  onEditMessageButtonClick: () => void;
  onRefresh: () => void;
  showEnvironmentPicker?: boolean;
}

const MaintenanceMessageToolbar = ({
  onEnvironmentSelected,
  onStartMaintenanceButtonClick,
  onEndMaintenanceButtonClick,
  onEditMessageButtonClick,
  onRefresh,
  showEnvironmentPicker = true,
}: MaintenanceMessageToolbarProps) => {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h5" color="text.secondary">
          Maintenance
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {showEnvironmentPicker && (
            <Box sx={{ minWidth: 250 }}>
              <EnvironmentPicker onlyDevEnvs={true} onEnvironmentSelected={onEnvironmentSelected} />
            </Box>
          )}
          <Button variant="contained" color="warning" startIcon={<PlayArrowIcon />} onClick={onStartMaintenanceButtonClick}>
            Start Maintenance
          </Button>
          <Button variant="contained" color="success" startIcon={<StopIcon />} onClick={onEndMaintenanceButtonClick}>
            End Maintenance
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh}>
          Refresh
        </Button>
        <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={onEditMessageButtonClick}>
          Edit Message
        </Button>
      </Box>
    </Box>
  );
};

export interface MaintenanceMessageProps {
  maintenanceMessage: IMaintenanceFile | undefined;
  selectedEnvironment: IEnvironment | undefined;
  onEnvironmentSelected: (environment: IEnvironment | undefined) => void;
  onStartMaintenanceButtonClick: () => void;
  onEndMaintenanceButtonClick: () => void;
  onEditMessageButtonClick: () => void;
  onRefresh: () => void;
  backgroundColor?: string;
  showEnvironmentPicker?: boolean;
}

export const MaintenanceMessage = ({
  maintenanceMessage,
  selectedEnvironment,
  onEnvironmentSelected,
  onStartMaintenanceButtonClick,
  onEndMaintenanceButtonClick,
  onEditMessageButtonClick,
  onRefresh,
  backgroundColor,
  showEnvironmentPicker = true,
}: MaintenanceMessageProps) => {
  let color: "error" | "success" | "warning" = "success";
  if (maintenanceMessage) {
    if (maintenanceMessage.status === MaintenanceStatus.IN_PROGRESS) {
      color = "error";
    } else if (maintenanceMessage.status === MaintenanceStatus.SCHEDULED) {
      color = "warning";
    }
  }
  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <Card variant="outlined" sx={{ bgcolor: backgroundColor }}>
          <CardContent>
            <MaintenanceMessageToolbar
              selectedEnvironment={selectedEnvironment}
              onEnvironmentSelected={onEnvironmentSelected}
              onStartMaintenanceButtonClick={onStartMaintenanceButtonClick}
              onEndMaintenanceButtonClick={onEndMaintenanceButtonClick}
              onEditMessageButtonClick={onEditMessageButtonClick}
              onRefresh={onRefresh}
              showEnvironmentPicker={showEnvironmentPicker}
            />
            {maintenanceMessage && (
              <Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <Chip label={maintenanceMessage.status?.toUpperCase() || "UNKNOWN"} color={color} size="small" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Start Time
                    </Typography>
                    <Typography variant="body1">
                      {maintenanceMessage.maintenanceStartedAt
                        ? DateTime.fromSeconds(maintenanceMessage.maintenanceStartedAt).toLocaleString(
                            DateTime.DATETIME_SHORT_WITH_SECONDS,
                          )
                        : "-"}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      End Time
                    </Typography>
                    <Typography variant="body1">
                      {maintenanceMessage.maintenanceEndsAt
                        ? DateTime.fromSeconds(maintenanceMessage.maintenanceEndsAt).toLocaleString(
                            DateTime.DATETIME_SHORT_WITH_SECONDS,
                          )
                        : "-"}
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Default Message
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Typography variant="body2">{maintenanceMessage.defaultMessage || "-"}</Typography>
                    </Paper>
                  </Grid>
                  {maintenanceMessage.localizedMessages && Object.keys(maintenanceMessage.localizedMessages).length > 0 && (
                    <Grid size={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Localized Messages
                      </Typography>
                      <Grid container spacing={2}>
                        {Object.entries(maintenanceMessage.localizedMessages).map(([locale, message]) => (
                          <Grid size={{ xs: 12, md: 6 }} key={locale}>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: "bold", mb: 0.5, display: "block" }}
                              >
                                {locale.toUpperCase()}
                              </Typography>
                              <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                                <Typography variant="body2">{message}</Typography>
                              </Paper>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      </Paper>
    </Box>
  );
};
